// src/common/firebase/FirestoreService.ts
import { firestore } from "./Firebase";
import { collection, query, where, addDoc, getDocs, getDoc, orderBy, limit, doc, updateDoc, deleteDoc, runTransaction } from "firebase/firestore";
import AdminAccount from "../../model/auth/AdminAccount";
import EmailAuth from "../../model/auth/EmailAuth";
import Copyright from "../../model/homepage/Copyright";
import Post from "../../model/archive/Post";
import CustomerInquiry from "../../model/homepage/CustomerInquiry";
import CustomerReport from "../../model/homepage/CustomerReport";
import ContactPerson, { ContactWorkType } from "../../model/homepage/ContactPerson";
import Recruit from "../../model/board/Recruit";
import TabManager from "../../model/board/TabManager";
import Patent from "../../model/board/Patent";
import Marketing from "../../model/board/Marketing";
import Notice from "../../model/board/Notice";

/**
 * 회원가입 회원 데이터 생성
 * @async
 * @function addAdminAccount
 * @param {AdminAccount} adminAccount - 관리자 계정 정보 (입력 form)
 * @returns 
 * @throws {Error}
 */
const addAdminAccount = async (adminAccount: AdminAccount) => {
    const docRef = await addDoc(collection(firestore, "adminAccount"), {
        username: adminAccount.username,
        email: adminAccount.email,
        isWithdrawal: adminAccount.isWithdrawal,
        isSuperUser: adminAccount.isSuperUser,
        createdBy: adminAccount.createdBy,
        createdAt: adminAccount.createdAt
    });

    return docRef.id;
};

/**
 * Username을 통한 관리자 계정 정보 조회
 * @async
 * @function fetchAdminAccountByUsername
 * @param {string} username - 사용자 ID
 * @returns {Promise<AdminAccount | null>}
 * @throws {Error}
 */
const fetchAdminAccountByUsername = async (username: string): Promise<AdminAccount | null> => {
    const adminAccountRef = collection(firestore, "adminAccount");
    const fetchByUserIdQuery = query(adminAccountRef, where("username", "==", username));
    const querySnapshot = await getDocs(fetchByUserIdQuery);

    if (querySnapshot.empty) {
        return null;
    }

    const id = querySnapshot.docs[0].id;
    const data = querySnapshot.docs[0].data();
    const adminAccount: AdminAccount = {
        id: id,
        username: data.username,
        email: data.email,
        isWithdrawal: data.isWithdrawal,
        isSuperUser: data.isSuperUser || false,
        createdAt: data.createdAt,
        createdBy: data.createdBy 
    }

    return adminAccount;
};

/**
 * Email을 통한 관리자 계정 정보 조회
 * @async
 * @function fetchAdminAccountByUsername
 * @param {string} username - 사용자 ID
 * @returns {Promise<AdminAccount | null>}
 * @throws {Error}
 */
const fetchAdminAccountByEmail = async (email: string): Promise<AdminAccount | null> => {
    const adminAccountRef = collection(firestore, "adminAccount");
    const fetchByEmailQuery = query(adminAccountRef, where("email", "==", email));
    const querySnapshot = await getDocs(fetchByEmailQuery);

    if (querySnapshot.empty) {
        return null;
    }

    const id = querySnapshot.docs[0].id;
    const data = querySnapshot.docs[0].data();
    const adminAccount: AdminAccount = {
        id: id,
        username: data.username,
        email: data.email,
        isWithdrawal: data.isWithdrawal,
        isSuperUser: data.isSuperUser || false,
        createdAt: data.createdAt,
        createdBy: data.createdBy 
    }

    return adminAccount;
};

/**
 * Email 및 Username을 통한 관리자 계정 정보 조회
 * @async
 * @function fetchAdminAccountByUsername
 * @param {string} username - 사용자 ID
 * @returns {Promise<AdminAccount | null>}
 * @throws {Error}
 */
const fetchAdminAccountByEmailAndUsername = async (email: string, username: string): Promise<AdminAccount | null> => {
    const adminAccountRef = collection(firestore, "adminAccount");
    const fetchByEmailAndUserIdQuery = query(adminAccountRef, where("email", "==", email), where("username", "==", username));
    const querySnapshot = await getDocs(fetchByEmailAndUserIdQuery);

    if (querySnapshot.empty) {
        return null;
    }

    const id = querySnapshot.docs[0].id;
    const data = querySnapshot.docs[0].data();
    const adminAccount: AdminAccount = {
        id: id,
        username: data.username,
        email: data.email,
        isWithdrawal: data.isWithdrawal,
        isSuperUser: data.isSuperUser || false,
        createdAt: data.createdAt,
        createdBy: data.createdBy 
    }

    return adminAccount;
};

/**
 * 전체 관리자 계정 정보 조회
 * @async
 * @function fetchAllAdminAccounts
 * @returns {Promise<AdminAccount[]>}
 * @throws {Error}
 */
const fetchAllAdminAccountsOrderByCreatedAt = async (): Promise<AdminAccount[]> => {
    const adminAccountRef = collection(firestore, "adminAccount");
    const filterWithdrawalQuery = query(adminAccountRef, where("isWithdrawal", "==", false));
    const querySnapshot = await getDocs(filterWithdrawalQuery);

    const adminAccounts: AdminAccount[] = querySnapshot.docs.map((doc) => {
        const id = doc.id;
        const data = doc.data();
        return {
            id: id,
            username: data.username,
            email: data.email,
            isWithdrawal: data.isWithdrawal,
            isSuperUser: data.isSuperUser || false,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(), // 여기서 변환
        };
    });

    // 🔽 createdAt 기준 오름차순 정렬
    adminAccounts.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    return adminAccounts;
}

/**
 * 관리자 계정의 슈퍼 관리자 권한 업데이트
 * @async
 * @function updateAdminAccountSuperUser
 * @param {string} email - 이메일
 * @param {boolean} isSuperUser - 슈퍼 관리자 여부
 * @returns {Promise<void>}
 * @throws {Error}
 */
const updateAdminAccountSuperUser = async (email: string, username: string, isSuperUser: boolean): Promise<void> => {
    // 이메일로 관리자 계정 조회
    const adminAccount = await fetchAdminAccountByEmailAndUsername(email, username);
        
    if (!adminAccount) {
        throw new Error("해당 이메일 / 아이디에 해당하는 관리자 계정을 찾을 수 없습니다.");
    }

    // Firestore 문서 참조 생성
    const adminAccountRef = doc(firestore, "adminAccount", adminAccount.id);
    
    // isSuperUser 필드 업데이트
    await updateDoc(adminAccountRef, {
        isSuperUser: isSuperUser
    });
};

const fetchLatestEmailAuth = async (email: string): Promise<EmailAuth | null> => {
    const emailAuthRef = collection(firestore, "emailAuth");
    const latestEmailAuthQuery = query(
        emailAuthRef,
        where("email", "==", email),
        orderBy("createdAt", "desc"),
        limit(1)
    );
    const querySnapshot = await getDocs(latestEmailAuthQuery);

    if (querySnapshot.empty) {
        return null;
    } else {
        const id = querySnapshot.docs[0].id;
        const data = querySnapshot.docs[0].data();
        const emailAuth: EmailAuth = {
            id: id,
            email: data.email,
            authCode: data.authCode,
            verified: data.verified,
            verificationId: data.verificationId,
            expiredAt: data.expiredAt.toDate(),
            verifiedAt: data.verifiedAt?.toDate(),
            createdAt: data.createdAt.toDate()
        }

        return emailAuth;
    }
}

const verifyEmailAuth = async (docId: string, verificationId: string): Promise<void> => {
    const docRef = doc(firestore, "emailAuth", docId);
    const now = new Date();
    await updateDoc(
        docRef,
        {
            verified: true,
            verificationId: verificationId,
            verifiedAt: now
        }
    );
}

const fetchCopyright = async (): Promise<Copyright> => {
    const copyrightRef = collection(firestore, "copyright");
    const querySnapshot = await getDocs(copyrightRef);

    if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();

        return {
            companyName: data.companyName ?? '',
            address: data.address ?? '',
            corpRegNo: data.corpRegNo ?? '',
            representPersonName: data.representPersonName ?? '',
            copyright: data.copyright ?? '',
        };
    }

    // 문서가 없을 경우 기본값 반환
    return {
        companyName: '',
        address: '',
        corpRegNo: '',
        representPersonName: '',
        copyright: '',
    };
};

const updateCopyright = async (data: Copyright): Promise<void> => {
    const copyrightRef = collection(firestore, "copyright");
    const querySnapshot = await getDocs(copyrightRef);

    const doc = querySnapshot.docs[0];

    if (doc) {
        // 문서가 존재하면 update
        await updateDoc(doc.ref, {
            companyName: data.companyName,
            address: data.address,
            corpRegNo: data.corpRegNo,
            representPersonName: data.representPersonName,
            copyright: data.copyright,
        });
    } else {
        // 문서가 없으면 새로 생성
        await addDoc(copyrightRef, {
            companyName: data.companyName,
            address: data.address,
            corpRegNo: data.corpRegNo,
            representPersonName: data.representPersonName,
            copyright: data.copyright,
        });
    }
};

const fetchPosts = async (boardName: string): Promise<Post[]> => {
    const postRef = collection(firestore, boardName);
    const q = query(postRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const posts: Post[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
            id: doc.id, // ← 문서 ID를 포함시킴
            boardType: data.boardType,
            title: data.title,
            representImage: data.representImage || '',
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate,
            isNotice: data.isNotice,
            isHidden: data.isHidden,
            isDeleted: data.isDeleted,
            createdBy: data.createdBy || '',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedBy: data.updatedBy || '',
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
        };
    });

    return posts;
};

const fetchPost = async (boardName: string, id: string): Promise<Post | null> => {
    try {
        const postDocRef = doc(firestore, boardName, id); // 문서 참조 생성
        const docSnap = await getDoc(postDocRef); // 문서 가져오기

        if (docSnap.exists()) {
            const data = docSnap.data();

            const post: Post = {
                id: docSnap.id,
                boardType: data.boardType,
                title: data.title,
                representImage: data.representImage || '',
                contents: data.contents,
                attachedFiles: data.attachedFiles || [],
                postDate: data.postDate,
                isNotice: data.isNotice,
                isHidden: data.isHidden,
                isDeleted: data.isDeleted,
                createdBy: data.createdBy || '',
                createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
                updatedBy: data.updatedBy || '',
                updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
            };

            return post;
        } else {
            console.warn("해당 게시글을 찾을 수 없습니다.");
            return null;
        }
    } catch (error) {
        console.error("게시글 조회 중 오류 발생:", error);
        return null;
    }
};

const updatePost = async (boardName: string, data: Post): Promise<void> => {
    try {
        if (!data.id) {
            throw new Error("문서 ID가 없어 게시글을 업데이트할 수 없습니다.");
        }

        const targetRef = doc(firestore, boardName, data.id); // 🔥 ID 기반 접근

        await updateDoc(targetRef, {
            boardType: data.boardType,
            title: data.title,
            representImage: data.representImage || '',
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate,
            isNotice: data.isNotice,
            isHidden: data.isHidden,
            isDeleted: data.isDeleted,
            createdBy: data.createdBy || '',
            createdAt: data.createdAt,
            updatedBy: data.updatedBy || '',
            updatedAt: data.updatedAt,
        });

        console.log("✅ 게시글이 성공적으로 업데이트되었습니다.");
    } catch (error) {
        console.error("❌ 게시글 업데이트 중 오류 발생:", error);
        throw error;
    }
};

const createPost = async (data: Post): Promise<void> => {
    try {
        const postRef = collection(firestore, "post");

        const payload = {
            boardType: data.boardType,
            title: data.title,
            representImage: data.representImage || '',
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate,
            isNotice: data.isNotice,
            isHidden: data.isHidden,
            isDeleted: data.isDeleted,
            createdBy: data.createdBy || '',
            createdAt: data.createdAt,
            updatedBy: data.updatedBy || '',
            updatedAt: data.updatedAt,
        };

        await addDoc(postRef, payload);
        console.log("✅ 새 게시글이 성공적으로 추가되었습니다.");
    } catch (error) {
        console.error("❌ 게시글 추가 중 오류 발생:", error);
        throw error;
    }
};

const deletePost = async (boardName: string, id: string): Promise<boolean> => {
    try {
        const postDocRef = doc(firestore, boardName, id);
        await deleteDoc(postDocRef);
        console.log(`게시글 ${id} 삭제 완료`);
        return true;
    } catch (error) {
        console.error(`게시글 삭제 중 오류 발생 (id: ${id}):`, error);
        return false;
    }
};

const fetchCustomerReports = async (): Promise<CustomerReport[]> => {
    const customerReportRef = collection(firestore, "customerReport");
    const q = query(customerReportRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const customerReports: CustomerReport[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
            id: doc.id, // ← 문서 ID를 포함시킴
            requestorName: data.requestorName,
            dateOfBirth: data.dateOfBirth.toDate(),
            mobileNo: data.mobileNo,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyTitle: data.replyTitle,
            replyContents: data.replyContents,
            collectInfoAgreeYn: data.collectInfoAgreeYn,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt.toDate()
        };
    });

    return customerReports;
};

const fetchCustomerReport = async (id: string): Promise<CustomerReport | null> => {
    try {
        const customerReportDocRef = doc(firestore, "customerReport", id); // 문서 참조 생성
        const docSnap = await getDoc(customerReportDocRef); // 문서 가져오기

        if (docSnap.exists()) {
            const data = docSnap.data();

            const customerReport: CustomerReport = {
                id: docSnap.id, // ← 문서 ID를 포함시킴
                requestorName: data.requestorName,
                dateOfBirth: data.dateOfBirth.toDate(),
                mobileNo: data.mobileNo,
                email: data.email,
                title: data.title,
                contents: data.contents,
                replyTitle: data.replyTitle,
                replyContents: data.replyContents,
                collectInfoAgreeYn: data.collectInfoAgreeYn,
                replyCompletedYn: data.replyCompletedYn,
                createdAt: data.createdAt.toDate()
            };

            return customerReport;
        } else {
            console.warn("해당 신고건을 찾을 수 없습니다.");
            return null;
        }
    } catch (error) {
        console.error("신고건 조회 중 오류 발생:", error);
        return null;
    }
};

const updateCustomerReport = async (id: string, data: CustomerReport): Promise<void> => {
    try {
        if (!data.id) {
            throw new Error("문서 ID가 없어 게시글을 업데이트할 수 없습니다.");
        }

        const targetRef = doc(firestore, "customerReport", data.id); // 🔥 ID 기반 접근

        await updateDoc(targetRef, {
            requestorName: data.requestorName,
            dateOfBirth: data.dateOfBirth,
            mobileNo: data.mobileNo,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyTitle: data.replyTitle,
            replyContents: data.replyContents,
            collectInfoAgreeYn: data.collectInfoAgreeYn,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt
        });

        console.log("✅ 신고건이 성공적으로 업데이트되었습니다.");
    } catch (error) {
        console.error("❌ 신고건 업데이트 중 오류 발생:", error);
        throw error;
    }
};

const createCustomerReport = async (data: CustomerReport): Promise<void> => {
    try {
        const customerReportRef = collection(firestore, "customerReport");

        const payload = {
            requestorName: data.requestorName,
            dateOfBirth: data.dateOfBirth,
            mobileNo: data.mobileNo,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyTitle: data.replyTitle,
            replyContents: data.replyContents,
            collectInfoAgreeYn: data.collectInfoAgreeYn,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt
        };

        await addDoc(customerReportRef, payload);
        console.log("✅ 새 신고건이 성공적으로 추가되었습니다.");
    } catch (error) {
        console.error("❌ 신고건 추가 중 오류 발생:", error);
        throw error;
    }
};

const fetchCustomerInquiries = async (): Promise<CustomerInquiry[]> => {
    const inquiryRef = collection(firestore, "customerInquiry");
    const q = query(inquiryRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const inquiries: CustomerInquiry[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
            id: doc.id, // ← 문서 ID를 포함시킴
            requestorName: data.requestorName,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt.toDate()
        };
    });

    return inquiries;
};

const fetchCustomerInquiry = async (id: string): Promise<CustomerInquiry | null> => {
    try {
        const inquiryDocRef = doc(firestore, "customerInquiry", id); // 문서 참조 생성
        const docSnap = await getDoc(inquiryDocRef); // 문서 가져오기

        if (docSnap.exists()) {
            const data = docSnap.data();

            const inquiry: CustomerInquiry = {
                id: docSnap.id, // ← 문서 ID를 포함시킴
                requestorName: data.requestorName,
                email: data.email,
                title: data.title,
                contents: data.contents,
                replyCompletedYn: data.replyCompletedYn,
                createdAt: data.createdAt.toDate()
            };

            return inquiry;
        } else {
            console.warn("해당 문의건 찾을 수 없습니다.");
            return null;
        }
    } catch (error) {
        console.error("문의건 조회 중 오류 발생:", error);
        return null;
    }
};

const updateCustomerInquiry = async (id: string, data: CustomerInquiry): Promise<void> => {
    try {
        if (!data.id) {
            throw new Error("문서 ID가 없어 게시글을 업데이트할 수 없습니다.");
        }

        const targetRef = doc(firestore, "customerInquiry", data.id); // 🔥 ID 기반 접근

        await updateDoc(targetRef, {
            requestorName: data.requestorName,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt
        });

        console.log("✅ 문의건이 성공적으로 업데이트되었습니다.");
    } catch (error) {
        console.error("❌ 문의건 업데이트 중 오류 발생:", error);
        throw error;
    }
};

const createCustomerInquiry = async (data: CustomerInquiry): Promise<void> => {
    try {
        const inquiryRef = collection(firestore, "customerInquiry");

        const payload = {
            requestorName: data.requestorName,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt
        };

        await addDoc(inquiryRef, payload);
        console.log("✅ 새 문의건이 성공적으로 추가되었습니다.");
    } catch (error) {
        console.error("❌ 문의건 추가 중 오류 발생:", error);
        throw error;
    }
};

const fetchContactPersons = async (): Promise<ContactPerson[]> => {
    const contactpersonRef = collection(firestore, "contactperson");
    const q = query(contactpersonRef);
    const querySnapshot = await getDocs(q);

    const contactpersons: ContactPerson[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const worktype = ContactWorkType.from(data.worktype)

        return {
            id: doc.id, // ← 문서 ID를 포함시킴
            worktype: worktype,
            workname: data.workname,
            name: data.name,
            email: data.email,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
        };
    });

    return contactpersons;
};

const fetchContactPerson = async (worktype: ContactWorkType): Promise<ContactPerson> => {
    const contactpersonRef = collection(firestore, 'contactperson');
    const q = query(contactpersonRef, where('worktype', '==', worktype));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
        throw new Error(`No ContactPerson found for worktype: ${worktype}`);
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();

    return {
        id: doc.id,
        worktype: ContactWorkType.from(data.worktype),
        workname: data.workname,
        name: data.name,
        email: data.email,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
    };
};

const updateContactPerson = async (id: string, data: ContactPerson): Promise<void> => {
    try {
        if (!data.id) {
            throw new Error("문서 ID가 없어 담당자 정보를 업데이트할 수 없습니다.");
        }

        const targetRef = doc(firestore, "contactperson", data.id); // 🔥 ID 기반 접근

        await updateDoc(targetRef, {
            worktype: data.worktype,
            workname: data.workname,
            name: data.name,
            email: data.email,
            createdAt: data.createdAt,
            updatedAt: new Date(),
        });

        console.log("✅ 담당자 정보가 성공적으로 업데이트되었습니다.");
    } catch (error) {
        console.error("❌ 담당자 정보 업데이트 중 오류 발생:", error);
        throw error;
    }
};

// 🔍 Recruit 목록 조회
const fetchRecruits = async (): Promise<Recruit[]> => {
    const recruitRef = collection(firestore, "recruit");
    const q = query(recruitRef, orderBy("jobTitle", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                jobType: data.jobType,
                jobTitle: data.jobTitle,
                contents: data.contents,
                recruitStartDate: data.recruitStartDate.toDate(),
                recruitEndDate: data.recruitEndDate?.toDate(),
                isPublished: data.isPublished,
                attachedFiles: data.attachedFiles,
                createdBy: data.createdBy,
                createdAt: data.createdAt.toDate(),
                updatedBy: data.updatedBy,
                updatedAt: data.updatedAt.toDate(),
            };
    });
};

// 🔍 Recruit 단일 조회
const fetchRecruit = async (id: string): Promise<Recruit | null> => {
    const docRef = doc(firestore, "recruit", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            jobType: data.jobType,
            jobTitle: data.jobTitle,
            contents: data.contents,
            recruitStartDate: data.recruitStartDate.toDate(),
            recruitEndDate: data.recruitEndDate?.toDate(),
            isPublished: data.isPublished,
            attachedFiles: data.attachedFiles,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Recruit 문서를 찾을 수 없습니다.");
        return null;
    }
};

// ✏️ Recruit 생성
const createRecruit = async (data: Recruit): Promise<string> => {
    const recruitRef = collection(firestore, "recruit");
    const docRef = await addDoc(recruitRef, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
    });

    return docRef.id;
};

// 🛠️ Recruit 수정
const updateRecruit = async (data: Recruit): Promise<void> => {
    if (!data.id) throw new Error("📛 ID가 없어 수정할 수 없습니다.");

    const recruitRef = doc(firestore, "recruit", data.id);
    const { id, ...updateData } = data;

    await updateDoc(recruitRef, {
        ...updateData,
        updatedAt: data.updatedAt,
    });
};

// ❌ Recruit 삭제
const deleteRecruitsInTransaction = async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0) return true;

    try {
        await runTransaction(firestore, async (transaction) => {
        for (const id of ids) {
            const recruitRef = doc(firestore, "recruit", id);
            transaction.delete(recruitRef);
        }
        });

        console.log("✅ 모든 Recruit 문서 삭제 완료 (트랜잭션)");
        return true;
    } catch (error) {
        console.error("❌ 트랜잭션 실패, Recruit 문서 삭제 안됨", error);
        return false;
    }
};

// 🔍 Patent 목록 조회
const fetchPatents = async (): Promise<Patent[]> => {
    const patentRef = collection(firestore, "patent");
    const q = query(patentRef, orderBy("title", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            patentType: data.patentType,
            title: data.title,
            englishTitle: data.englishTitle,
            chineseTitle: data.chineseTitle,
            representImage: data.representImage,
            acquisitionDate: data.acquisitionDate?.toDate(),
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    });
};

// 🔍 Patent 단일 조회
const fetchPatent = async (id: string): Promise<Patent | null> => {
    const docRef = doc(firestore, "patent", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            patentType: data.patentType,
            title: data.title,
            englishTitle: data.englishTitle,
            chineseTitle: data.chineseTitle,
            representImage: data.representImage,
            acquisitionDate: data.acquisitionDate?.toDate(),
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Patent 문서를 찾을 수 없습니다.");
        return null;
    }
};

// ✏️ Patent 생성
const createPatent = async (data: Patent): Promise<string> => {
    const patentRef = collection(firestore, "patent");
    const docRef = await addDoc(patentRef, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
    });

    return docRef.id;
};

// 🛠️ Patent 수정
const updatePatent = async (data: Patent): Promise<void> => {
    if (!data.id) throw new Error("📛 ID가 없어 수정할 수 없습니다.");

    const patentRef = doc(firestore, "patent", data.id);
    const { id, ...updateData } = data;

    await updateDoc(patentRef, {
        ...updateData,
        updatedAt: data.updatedAt,
    });
};

// ❌ Patent 삭제 (트랜잭션 기반)
const deletePatentsInTransaction = async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0) return true;

    try {
        await runTransaction(firestore, async (transaction) => {
            for (const id of ids) {
                const patentRef = doc(firestore, "patent", id);
                transaction.delete(patentRef);
            }
        });

        console.log("✅ 모든 Patent 문서 삭제 완료 (트랜잭션)");
        return true;
    } catch (error) {
        console.error("❌ 트랜잭션 실패, Patent 문서 삭제 안됨", error);
        return false;
    }
};

// Marketing List 조회
const fetchMarketings = async (): Promise<Marketing[]> => {
    const marketingRef = collection(firestore, "marketing");
    const q = query(marketingRef, orderBy("postDate", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            marketingType: data.marketingType,
            title: data.title,
            representImage: data.representImage,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate?.toDate() || null,
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    });
};

// 단일 Marketing 조회
const fetchMarketing = async (id: string): Promise<Marketing | null> => {
    const docRef = doc(firestore, "marketing", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            marketingType: data.marketingType,
            title: data.title,
            representImage: data.representImage,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate?.toDate() || null,
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Marketing 문서를 찾을 수 없습니다.");
        return null;
    }
};

// Marketing 생성
const createMarketing = async (data: Marketing): Promise<string> => {
    const marketingRef = collection(firestore, "marketing");
    const docRef = await addDoc(marketingRef, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
    });

    return docRef.id;
};

// Marketing 수정
const updateMarketing = async (data: Marketing): Promise<void> => {
    if (!data.id) throw new Error("📛 ID가 없어 수정할 수 없습니다.");

    const marketingRef = doc(firestore, "marketing", data.id);
    const { id, ...updateData } = data;

    await updateDoc(marketingRef, {
        ...updateData,
        updatedAt: new Date(),
    });
};

// Marketing 삭제
const deleteMarketingsInTransaction = async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0) return true;

    try {
        await runTransaction(firestore, async (transaction) => {
            for (const id of ids) {
                const marketingRef = doc(firestore, "marketing", id);
                transaction.delete(marketingRef);
            }
        });

        console.log("✅ 모든 Marketing 문서 삭제 완료 (트랜잭션)");
        return true;
    } catch (error) {
        console.error("❌ 트랜잭션 실패, Marketing 문서 삭제 안됨", error);
        return false;
    }
};

// 공지사항 목록 조회
const fetchNotices = async (): Promise<Notice[]> => {
    const noticeRef = collection(firestore, "notice");
    const q = query(noticeRef, orderBy("postDate", "desc"), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            title: data.title,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate.toDate(),
            isPublished: data.isPublished,
            isAnchored: data.isAnchored,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    });
};

// 단일 공지사항 조회
const fetchNotice = async (id: string): Promise<Notice | null> => {
    const docRef = doc(firestore, "notice", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            title: data.title,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate.toDate(),
            isPublished: data.isPublished,
            isAnchored: data.isAnchored,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Notice 문서를 찾을 수 없습니다.");
        return null;
    }
};

// 공지사항 생성
const createNotice = async (data: Notice): Promise<string> => {
    const noticeRef = collection(firestore, "notice");
    const docRef = await addDoc(noticeRef, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date(),
    });

    return docRef.id;
};

// 공지사항 수정
const updateNotice = async (data: Notice): Promise<void> => {
    if (!data.id) throw new Error("📛 ID가 없어 수정할 수 없습니다.");

    const noticeRef = doc(firestore, "notice", data.id);
    const { id, ...updateData } = data;

    await updateDoc(noticeRef, {
        ...updateData,
        updatedAt: new Date(),
    });
};

// 공지사항 다건 삭제
const deleteNoticesInTransaction = async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0) return true;

    try {
        await runTransaction(firestore, async (transaction) => {
            for (const id of ids) {
                const noticeRef = doc(firestore, "notice", id);
                transaction.delete(noticeRef);
            }
        });

        console.log("✅ 모든 Notice 문서 삭제 완료 (트랜잭션)");
        return true;
    } catch (error) {
        console.error("❌ 트랜잭션 실패, Notice 문서 삭제 안됨", error);
        return false;
    }
};

// 🔍 TabManager 목록 조회
const fetchTabManagers = async (screenName: string): Promise<TabManager[]> => {
    const tabRef = collection(firestore, "tabManager");
    const q = query(tabRef, where("screenName", "==", screenName), orderBy("tabOrder", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
        id: doc.id,
        screenName: data.screenName,
        tabCode: data.tabCode,
        tabName: data.tabName,
        createdBy: data.createdBy,
        createdAt: data.createdAt?.toDate(),
        updatedBy: data.updatedBy,
        updatedAt: data.updatedAt?.toDate(),
        };
    });
};

export {
    addAdminAccount, fetchAdminAccountByUsername, fetchAdminAccountByEmail, fetchAdminAccountByEmailAndUsername, fetchAllAdminAccountsOrderByCreatedAt, updateAdminAccountSuperUser,
    fetchLatestEmailAuth, verifyEmailAuth,
    fetchCopyright, updateCopyright,
    fetchPosts, fetchPost, updatePost, createPost, deletePost,
    fetchCustomerReports, fetchCustomerReport, updateCustomerReport, createCustomerReport,
    fetchCustomerInquiries, fetchCustomerInquiry, updateCustomerInquiry, createCustomerInquiry,
    fetchContactPersons, fetchContactPerson, updateContactPerson,
    fetchRecruits, fetchRecruit, createRecruit, updateRecruit, deleteRecruitsInTransaction,
    fetchPatents, fetchPatent, createPatent, updatePatent, deletePatentsInTransaction,
    fetchMarketings, fetchMarketing, createMarketing, updateMarketing, deleteMarketingsInTransaction,
    fetchNotices, fetchNotice, createNotice, updateNotice, deleteNoticesInTransaction,
    fetchTabManagers,
};